import cn from 'classnames';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, Link } from 'react-router';

import { PATHS } from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';
import { getImageSrc } from '@/shared/lib';
import { Avatar } from '@/shared/ui';

import { ensureRefreshImageSrc } from '@/entities/assets';
import { DoctorsAvatars } from '@/entities/doctors';
import { StudiesCounters } from '@/entities/patient';

import { patientDoctorsModel } from '@/features/patientDoctors';

import styles from './PatientListCard.module.scss';

type PatientListCardProps = {
  patientID: string;
  patientName: string;
  patientBirthDate: string;
  patientLastModified?: string;
  displayAssetID: string;
  className?: string;
  testID?: string;
  disableStudies?: boolean;
};

export const PatientListCard: FC<PatientListCardProps> = (props) => {
  const {
    testID,
    className,
    patientID,
    patientName,
    disableStudies,
    displayAssetID,
    patientBirthDate,
    patientLastModified,
  } = props;

  const { formatMessage } = useIntl();

  const allPatientDoctors = useAppSelector(
    patientDoctorsModel.selectors.selectAllPatientDoctors(patientID),
  );

  const isOnlyOneDoctor = allPatientDoctors?.length === 1;

  const isEmptyPreview = !displayAssetID;

  const moreThanFiveDoctors =
    allPatientDoctors && allPatientDoctors?.length > 5;

  const visibleDoctors = moreThanFiveDoctors
    ? allPatientDoctors.slice(0, 4)
    : allPatientDoctors;

  return (
    <div
      data-testid={testID}
      className={cn(styles.container, className)}
      data-component="patient-list-card"
    >
      <header className={styles.patientCardHeader}>
        <Link to={generatePath(PATHS.patientProfile_DEPRECATED, { patientID })}>
          <div
            className={cn(
              styles.previewContainer,
              isEmptyPreview && styles.empty,
            )}
          >
            {isEmptyPreview ? (
              <div className={styles.previewDefaultImage} />
            ) : (
              <img
                className={styles.preview}
                src={getImageSrc(displayAssetID, 'preview')}
                onError={ensureRefreshImageSrc}
                alt={formatMessage({
                  id: 'imgAltText.patientPreview',
                  defaultMessage: 'Patient preview',
                })}
              />
            )}
          </div>
        </Link>
      </header>

      <div className={styles.patientInfo}>
        <Link to={generatePath(PATHS.patientProfile_DEPRECATED, { patientID })}>
          <h4 data-ph-mask className={cn(styles.patientName, 'h4')}>
            {patientName}
          </h4>

          <p data-ph-mask className={cn(styles.date, 'p2')}>
            {formatMessage({
              id: 'patientInfo.DoB',
              defaultMessage: 'DoB',
            })}

            {': '}

            {patientBirthDate}
          </p>

          {patientLastModified && (
            <p className={cn(styles.date, 'p2')}>
              {formatMessage({
                id: 'patientInfo.lastModified',
                defaultMessage: 'Last modified',
              })}

              {': '}

              {patientLastModified}
            </p>
          )}

          <Avatar.Group className={cn(styles.avatars)}>
            {visibleDoctors &&
              visibleDoctors.map((doctor) => (
                <DoctorsAvatars
                  key={doctor?.UserID}
                  doctor={doctor}
                  isOnlyOneDoctor={isOnlyOneDoctor}
                />
              ))}

            {allPatientDoctors && visibleDoctors && moreThanFiveDoctors && (
              <Avatar
                count={allPatientDoctors.length - visibleDoctors.length}
              />
            )}
          </Avatar.Group>

          {!disableStudies && <StudiesCounters patientID={patientID} />}
        </Link>
      </div>
    </div>
  );
};
